/* VSS Manual Subcategory Carousel 1.0. No dependencies, requests or autoplay. */
(() => {
  const tag = 'vss-integrated-categories-v1';
  if (customElements.get(tag)) return;

  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      // Also works when Shopify replaces a section in the theme editor.
      queueMicrotask(() => { if (this.isConnected && !this.abort) this.init(); });
    }

    init() {
      this.track = this.querySelector('[data-subcats-track]');
      if (!this.track) return;
      this.cards = [...this.track.querySelectorAll('.vss-subcats__card')];
      this.controls = this.querySelector('[data-subcats-controls]');
      this.prev = this.querySelector('[data-subcats-prev]');
      this.next = this.querySelector('[data-subcats-next]');
      this.status = this.querySelector('[data-subcats-status]');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.prev.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
        clearTimeout(this.announceTimer);
        this.announceTimer = setTimeout(() => this.announce(), 200);
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => this.onKey(event), options);
      this.track.addEventListener('focusin', (event) => {
        const card = event.target.closest('.vss-subcats__card');
        if (card) this.reveal(card);
      }, options);
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => this.update());
        this.resize.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.update(), options);
      }
      this.update();
    }

    get rtl() { return getComputedStyle(this.track).direction === 'rtl'; }
    get max() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
    get position() {
      const value = this.rtl ? -this.track.scrollLeft : this.track.scrollLeft;
      return Math.min(this.max, Math.max(0, value));
    }

    update() {
      const overflow = this.max > 2;
      this.controls.hidden = !overflow;
      this.track.tabIndex = overflow ? 0 : -1;
      this.prev.disabled = !overflow || this.position <= 2;
      this.next.disabled = !overflow || this.position >= this.max - 2;
    }

    scrollToPosition(position, smooth = true) {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({
        left: Math.min(this.max, Math.max(0, position)) * (this.rtl ? -1 : 1),
        behavior: smooth && !reduced ? 'smooth' : 'instant'
      });
    }

    move(direction) {
      if ((direction < 0 && this.prev.disabled) || (direction > 0 && this.next.disabled)) return;
      const step = this.cards[0].parentElement.getBoundingClientRect().width +
        (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const page = Math.max(1, Math.floor((this.track.clientWidth - 8) / step));
      this.scrollToPosition(this.position + direction * page * step);
    }

    reveal(card) {
      const box = this.track.getBoundingClientRect();
      const item = card.getBoundingClientRect();
      const delta = item.left < box.left + 4 ? item.left - box.left - 4 :
        item.right > box.right - 4 ? item.right - box.right + 4 : 0;
      if (delta) this.scrollToPosition(this.position + delta * (this.rtl ? -1 : 1), false);
    }

    onKey(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const card = event.target.closest('.vss-subcats__card');
      if (card) {
        let index = this.cards.indexOf(card);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.cards.length - 1;
        else index += (event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1);
        index = Math.max(0, Math.min(this.cards.length - 1, index));
        event.preventDefault();
        this.cards[index].focus({ preventScroll: true });
        this.reveal(this.cards[index]);
      } else if (event.target === this.track) {
        event.preventDefault();
        if (event.key === 'Home') this.scrollToPosition(0);
        else if (event.key === 'End') this.scrollToPosition(this.max);
        else this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1));
      }
    }

    announce() {
      if (!this.isConnected || this.controls.hidden) return;
      const box = this.track.getBoundingClientRect();
      const visible = this.cards.map((card, index) => {
        const item = card.getBoundingClientRect();
        const overlap = Math.min(item.right, box.right) - Math.max(item.left, box.left);
        return overlap >= item.width / 2 ? index + 1 : 0;
      }).filter(Boolean);
      if (visible.length) this.status.textContent = `Categories ${visible[0]}–${visible[visible.length - 1]} of ${this.cards.length}`;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      clearTimeout(this.announceTimer);
    }
  });
})();
